import { useContext, useMemo } from 'react';

import { ELIGIBILITY_CHECK_FIELDS } from '../constants/eligibilityCheckFields';
import { OnboardingStepsContext } from '../contexts/OnboardingStepsContext';
import { RulesEngineContext } from '../contexts/RulesEngineContext';
import type { GoToNextStepOptions } from '../types/onboardingStep';
import { hrefPathname } from '../utils/hrefPathname';
import {
  calculateProgress,
  canShowBack as canShowBackForPath,
  canShowHeader as canShowHeaderForPath,
} from '../utils/onboardingStepProgress';

export type UseOnboardingSteps = {
  /** 0–1 progress fraction for the current step, or null when the bar shouldn't render. */
  progress: number | null;
  /** Whether the back chevron should render for the current step. */
  canShowBack: boolean;
  /** Whether the persistent header chrome should render at all on this step. */
  canShowHeader: boolean;
  /**
   * Forward navigation command — computes the next route from current state
   * and applies it (the paired counterpart to `goBack`). Always available;
   * screens call it from a Continue press. Pass `{ from }` so the engine can
   * advance the static survey. Stable identity across renders.
   */
  goToNextStep: (options?: GoToNextStepOptions) => void;
  /**
   * Back navigation handler. `null` when the current step can't go back —
   * either the group is forward-only, or there's no in-group step in the
   * visited history (deep-link entry, etc.). Otherwise this is the rules
   * engine's `goBack` action, which pops `stepHistory` and updates
   * `currentRoute` so the layout's redirect carries the user backward.
   */
  goBack: (() => void) | null;
};

/**
 * Reactive view of the onboarding step config for the current step. Reads
 * `currentRoute` (RulesEngineContext) and `stepHistory` (OnboardingStepsContext)
 * rather than calling `usePathname()` so the hook stays inside the
 * rules-engine domain — per CLAUDE.md, the rules engine is the single
 * source of truth for "where the user should be", and pathname is just
 * the URL bar catching up.
 *
 * Back availability is history-driven (Sparta-style): we walk the visited
 * stack to find the most recent step in the current group. That handles
 * branches correctly — e.g. /company-search has two sibling next-steps
 * (/employer-or-health-plan-verification and /employee-zip-verification);
 * back from either must return to /company-search, not the *other* sibling.
 * A linear `stepIndex - 1` would walk through both.
 *
 * This is the single home for step navigation. Screens advance with
 * `goToNextStep()` (Continue presses) and the OnboardingHeader goes back with
 * `goBack()`. Forward nav lives in the rules engine (it needs domain state);
 * back nav + history live in OnboardingStepsContext (a pure function of
 * currentRoute). This hook recombines both so consumers see one nav surface.
 * (Eligibility / invite-code actions on `useRulesEngine` still route as a side
 * effect of their domain operation — see `runEligibilityCheck` /
 * `validateAndUseInviteCode`.)
 *
 * Reads both contexts directly (not via `useRulesEngine`) because the nav
 * members it needs are intentionally absent from that hook's surface.
 */
export function useOnboardingSteps(): UseOnboardingSteps {
  const rulesEngine = useContext(RulesEngineContext);
  if (!rulesEngine) {
    throw new Error('useOnboardingSteps must be used within a RulesEngineProvider');
  }
  const steps = useContext(OnboardingStepsContext);
  if (!steps) {
    throw new Error('useOnboardingSteps must be used within an OnboardingStepsProvider');
  }
  const { currentRoute, eligibilityCheckResult, goToNextStep, selectedOrg } = rulesEngine;
  const { stepHistory, goBack } = steps;

  return useMemo<UseOnboardingSteps>(() => {
    // Normalize to the bare path: `currentRoute` can be a string with a
    // `?query` (e.g. `/step/employee-search?org=boeing`) and the step lookup
    // table is keyed by bare paths.
    const path = hrefPathname(currentRoute) ?? '';

    // No path yet (cold start before bootstrap seeds currentRoute). The
    // onboarding layout shows LoadingScreen during this window, so returning
    // "no chrome" keeps the header dormant until the rules engine settles.
    // goToNextStep stays available so a screen mounted in this window can still
    // advance once state settles.
    if (!path) {
      return {
        progress: null,
        canShowBack: false,
        canShowHeader: false,
        goToNextStep,
        goBack: null,
      };
    }
    const isRteEligibilityChecking =
      eligibilityCheckResult?.isChecking === true &&
      (path === '/step/employee-zip-verification' ||
        (path === '/step/employee-search' &&
          selectedOrg?.eligibilityCheckFields ===
            ELIGIBILITY_CHECK_FIELDS.FN_LN_DOB_AND_EMPID_OR_ZC));
    const showHeader = canShowHeaderForPath(path) && !isRteEligibilityChecking;

    // Single gate: `canShowBackForPath` already checks the step's showHeader /
    // showBack flags AND walks history for an in-group previous step. When
    // it returns false, both the chevron and the action are unavailable.
    const showBackChevron = showHeader && canShowBackForPath(path, stepHistory);
    return {
      progress: showHeader ? calculateProgress(path) : null,
      canShowBack: showBackChevron,
      canShowHeader: showHeader,
      goToNextStep,
      goBack: showBackChevron ? goBack : null,
    };
  }, [
    currentRoute,
    eligibilityCheckResult?.isChecking,
    selectedOrg?.eligibilityCheckFields,
    stepHistory,
    goBack,
    goToNextStep,
  ]);
}
